import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dog, 
  Cat, 
  Plus, 
  Search, 
  Trash2,
  Eye,
  Clock,
  ChevronLeft,
  ChevronRight,
  Share2,
  HeartOff
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

import { BACKEND_URL } from '../config/api';
const PAGE_SIZE = 12;

const Cases = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [sharedPatients, setSharedPatients] = useState([]);
  const [showDeceased, setShowDeceased] = useState(false);

  const fetchPatients = useCallback(async (searchQuery = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * PAGE_SIZE).toString(),
        limit: PAGE_SIZE.toString(),
        deceased: showDeceased ? 'true' : 'false'
      });
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const [patientsRes, sharedRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/cases/patients?${params}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/cases/shared-with-me`, { withCredentials: true })
      ]);
      
      const items = patientsRes.data.items || patientsRes.data;
      setPatients(items);
      setTotalCount(patientsRes.data.total || items.length);
      setHasMore(patientsRes.data.has_more || false);
      setSharedPatients(sharedRes.data.items || []);
    } catch (error) {
      logger.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  }, [page, showDeceased]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    setPage(0);
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPatients(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchPatients]);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/cases/patients/${deleteId}`, {
        withCredentials: true
      });
      setPatients(patients.filter(p => p.id !== deleteId));
      setTotalCount(prev => prev - 1);
    } catch (error) {
      logger.error('Error deleting patient:', error);
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">Cases</h1>
          <p className="text-[#5C5F5C] mt-1">
            {showDeceased 
              ? `${totalCount} deceased patient${totalCount !== 1 ? 's' : ''}` 
              : totalCount > 0 ? `${totalCount} patient${totalCount !== 1 ? 's' : ''} total` : 'Manage your patient cases and neurological exams'}
          </p>
        </div>
        <Link to="/cases/new">
          <Button className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="new-patient-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Patient
          </Button>
        </Link>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F5C]" />
          <Input
            placeholder="Search by name, breed, or owner..."
            value={searchTerm}
            onChange={handleSearch}
            className="pl-10 bg-white border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
            data-testid="search-input"
          />
        </div>
        <Button
          variant={showDeceased ? "default" : "outline"}
          size="sm"
          onClick={() => { setShowDeceased(!showDeceased); setPage(0); }}
          className={showDeceased ? "bg-[#5C5F5C] hover:bg-[#4A4D4A] text-white gap-1.5" : "border-[#E5E4E0] text-[#5C5F5C] gap-1.5"}
          data-testid="toggle-deceased-btn"
        >
          <HeartOff className="w-4 h-4" />
          {showDeceased ? 'Showing Deceased' : 'Show Deceased'}
        </Button>
      </div>

      {/* Patients Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="border-[#E5E4E0]">
              <CardContent className="p-6">
                <div className="h-32 bg-[#F8F7F4] rounded-lg animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : patients.length > 0 ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {patients.map((patient) => (
            <Card 
              key={patient.id} 
              className="border-[#E5E4E0] bg-white card-hover"
              data-testid={`case-card-${patient.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    patient.species === 'dog' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
                  }`}>
                    {patient.species === 'dog' ? (
                      <Dog className="w-6 h-6 text-[#C06144]" />
                    ) : (
                      <Cat className="w-6 h-6 text-[#4B6651]" />
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Link to={`/cases/${patient.id}`}>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-[#5C5F5C] hover:text-[#4B6651]"
                        data-testid={`view-case-${patient.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#5C5F5C] hover:text-[#D94F4F]"
                      onClick={() => setDeleteId(patient.id)}
                      data-testid={`delete-case-${patient.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Link to={`/cases/${patient.id}`}>
                  <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-1 hover:text-[#4B6651]">
                    {patient.name}
                    {patient.is_deceased && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-[#5C5F5C]/10 text-[#5C5F5C] rounded font-normal align-middle" data-testid={`deceased-badge-${patient.id}`}>Deceased</span>
                    )}
                  </h3>
                </Link>
                <p className="text-sm text-[#5C5F5C] mb-2">
                  {patient.breed} • {patient.age} • {patient.weight}
                  {patient.sex && patient.sex !== 'unknown' && ` • ${patient.sex}`}
                </p>
                <p className="text-sm text-[#5C5F5C] mb-3">
                  Owner: {patient.owner_name}
                </p>
                <p className="text-sm text-[#5C5F5C] line-clamp-2 mb-4">
                  {patient.presenting_complaint}
                </p>
                <div className="flex items-center gap-1 text-xs text-[#5C5F5C]">
                  <Clock className="w-3 h-3" />
                  {new Date(patient.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4" data-testid="pagination">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-[#E5E4E0]"
              data-testid="prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-[#5C5F5C]">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              className="border-[#E5E4E0]"
              data-testid="next-page"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
        </>
      ) : (
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-12 text-center">
            <img
              src="https://images.pexels.com/photos/6816851/pexels-photo-6816851.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=300"
              alt="No patients"
              className="w-32 h-32 object-cover rounded-lg mx-auto mb-4 opacity-80"
            />
            <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
              {searchTerm ? 'No matching patients' : 'No patients yet'}
            </h3>
            <p className="text-[#5C5F5C] mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Start by adding your first patient case'
              }
            </p>
            {!searchTerm && (
              <Link to="/cases/new">
                <Button className="bg-[#4B6651] hover:bg-[#3E5543] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Patient
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Shared With Me */}
      {sharedPatients.length > 0 && (
        <div className="mt-8 space-y-4" data-testid="shared-patients-section">
          <h2 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#4B6651]" />
            Shared With Me
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sharedPatients.map((patient) => (
              <Card key={patient.id} className="border-[#E5E4E0] bg-white card-hover border-l-4 border-l-[#4B6651]" data-testid={`shared-case-${patient.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      patient.species === 'dog' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
                    }`}>
                      {patient.species === 'dog' ? <Dog className="w-6 h-6 text-[#C06144]" /> : <Cat className="w-6 h-6 text-[#4B6651]" />}
                    </div>
                    <span className="px-2 py-0.5 bg-[#4B6651]/10 text-[#4B6651] rounded text-xs flex items-center gap-1">
                      <Share2 className="w-3 h-3" />{patient.permission}
                    </span>
                  </div>
                  <Link to={`/cases/${patient.id}`}>
                    <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-1 hover:text-[#4B6651]">{patient.name}</h3>
                  </Link>
                  <p className="text-sm text-[#5C5F5C] mb-1">{patient.breed} • {patient.age}</p>
                  <p className="text-xs text-[#5C5F5C]">Shared by: {patient.shared_by_name}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Patient</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this patient? This will also remove all associated neurological exams and analyses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5E4E0]">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-[#D94F4F] hover:bg-[#D94F4F]/90"
              disabled={deleting}
              data-testid="confirm-delete"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Cases;
